import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useState, useEffect, Fragment, useCallback, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { DataTable, type TableColumnDef } from "../../components/data-table";
import { Button } from "../../components/ui/button";
import {
  Plus,
  Edit,
  Trash2,
  Upload,
  ChevronLeft,
  ChevronRight,
  Eye,
  ChevronDown,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  vendorsAPI,
  categoriesAPI,
  statesAPI,
  type Vendor,
  type VendorCreatePayload,
  type PastProject,
  type Category,
  type SubCategory,
  type State,
  PaginationParams,
} from "../../services/api";
import { CommonPagination } from "../../components/CommonPagination";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { useAuth } from "../../contexts/AuthContext";
import { VendorDetailsModal } from "../../components/vendors/VendorDetailsModal";
import { formatVendorArray } from "../../components/vendors/vendorUtils";

const STEPS = [
  { id: 1, title: "Basic Details" },
  { id: 2, title: "Contact Information" },
  { id: 3, title: "Service Coverage" },
  { id: 4, title: "Services & Experience" },
  { id: 5, title: "Verification" },
  { id: 6, title: "Availability" },
];

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Puducherry",
  "Chandigarh",
];

const WORKING_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const initialFormState: Record<string, unknown> = {
  vendor_name: "",
  category: [] as string[],
  sub_category: [] as string[],
  company_name: "",
  vendor_type: "" as "" | "individual" | "company",
  logo: null as File | null,
  short_description: "",
  contact_person_name: "",
  mobile_number: "",
  email: "",
  office_address: "",
  city: "",
  state: "",
  pin_code: "",
  service_areas: [] as string[],
  pan_india_service: "" as "" | "yes" | "no",
  services_offered: [] as string[],
  service_tag_input: "",
  specialization: "",
  years_of_experience: "",
  hostel_projects_experience: "" as "" | "yes" | "no",
  past_projects: "",
  past_projects_file: null as File | null,
  empanelment_status: "",
  verification_status: "",
  documents_uploaded: "",
  documents_uploaded_file: null as File | null,
  compliance_confirmation: false,
  admin_notes: "",
  available_for_work: "" as "" | "yes" | "no",
  working_days: [] as string[],
  service_timings_from: "",
  service_timings_to: "",
};

function VendorFormStep1({
  form,
  setForm,
  imagePreview,
  onImageChange,
  fieldErrors,
  setFieldErrors,
}: {
  form: typeof initialFormState;
  setForm: React.Dispatch<React.SetStateAction<typeof initialFormState>>;
  imagePreview: string | null;
  onImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fieldErrors: Record<string, string>;
  setFieldErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}) {
  const [categoriesList, setCategoriesList] = useState<Category[]>([]);
  const [subCategoriesList, setSubCategoriesList] = useState<SubCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [subCategoryOpen, setSubCategoryOpen] = useState(false);

  console.log(categoriesList, "categoriesList");
  const categoryIds = (form.category as string[]) || [];
  const subCategoryIds = (form.sub_category as string[]) || [];

  useEffect(() => {
    let cancelled = false;
    setLoadingCategories(true);
    categoriesAPI
      .list()
      .then(data => {
        if (!cancelled) {
          console.log(data, "data");
          const list = Array.isArray(data) ? data : ((data as any)?.data ?? []);
          setCategoriesList(list);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingCategories(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const categoryIdsKey = categoryIds.join(",");
  useEffect(() => {
    if (!categoryIds.length) {
      setSubCategoriesList([]);
      setForm(p => ({ ...p, sub_category: [] }));
      return;
    }
    let cancelled = false;
    setLoadingSubCategories(true);
    Promise.all(
      categoryIds.map(id => categoriesAPI.getSubCategoriesByCategoryIds([id]))
    )
      .then(results => {
        if (!cancelled) {
          setSubCategoriesList(
            Array.isArray(results.flat()) ? results.flat() : []
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingSubCategories(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIdsKey, setForm]);

  const toggleCategory = (id: string | number) => {
    const idStr = String(id);
    const next = categoryIds.includes(idStr)
      ? categoryIds.filter(c => c !== idStr)
      : [...categoryIds, idStr];
    setForm(p => ({ ...p, category: next, sub_category: [] }));
  };

  const toggleSubCategory = (id: string | number) => {
    const idStr = String(id);
    const current = (form.sub_category as string[]) || [];
    const next = current.includes(idStr)
      ? current.filter(s => s !== idStr)
      : [...current, idStr];
    setForm(p => ({ ...p, sub_category: next }));
  };

  const categoryLabel =
    categoryIds.length === 0
      ? "Select categories..."
      : categoriesList
          .filter(c => categoryIds.includes(String(c.id)))
          .map(c => c.name)
          .join(", ");
  const subCategoryLabel =
    subCategoryIds.length === 0
      ? "Select sub-categories..."
      : subCategoriesList
          .filter(s => subCategoryIds.includes(String(s.id)))
          .map(s => s.name)
          .join(", ");

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="vendor_name">
          Vendor Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id="vendor_name"
          placeholder="Enter vendor name"
          value={String(form.vendor_name)}
          required
          onChange={e => {
            const val = e.target.value;
            setForm(p => ({ ...p, vendor_name: val }));
            if (val.trim()) {
              setFieldErrors(p => {
                const next = { ...p };
                delete next.vendor_name;
                return next;
              });
            }
          }}
        />
        {fieldErrors.vendor_name && (
          <p className="mt-1 text-xs text-destructive">
            {fieldErrors.vendor_name}
          </p>
        )}
      </div>
      <div className="space-y-2">
        <Label>Category (Multi-select)</Label>
        <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-between font-normal"
              disabled={loadingCategories}
            >
              <span className="truncate">
                {loadingCategories ? "Loading..." : categoryLabel}
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <div className="max-h-60 overflow-y-auto p-2">
              {categoriesList.length === 0 && !loadingCategories ? (
                <p className="py-2 text-center text-sm text-muted-foreground">
                  No categories found
                </p>
              ) : (
                categoriesList.map(cat => (
                  <label
                    key={cat.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-muted"
                  >
                    <Checkbox
                      checked={categoryIds.includes(String(cat.id))}
                      onCheckedChange={() => toggleCategory(cat.id)}
                    />
                    <span className="text-sm">{cat.name}</span>
                  </label>
                ))
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div className="space-y-2">
        <Label>Sub-category (Multi-select)</Label>
        <Popover open={subCategoryOpen} onOpenChange={setSubCategoryOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-between font-normal"
              disabled={loadingSubCategories || categoryIds.length === 0}
            >
              <span className="truncate">
                {categoryIds.length === 0
                  ? "Select categories first"
                  : loadingSubCategories
                    ? "Loading..."
                    : subCategoryLabel}
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <div className="max-h-60 overflow-y-auto p-2">
              {subCategoriesList.length === 0 && !loadingSubCategories ? (
                <p className="py-2 text-center text-sm text-muted-foreground">
                  No sub-categories for selected categories
                </p>
              ) : (
                subCategoriesList.map(sub => (
                  <label
                    key={sub.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-muted"
                  >
                    <Checkbox
                      checked={subCategoryIds.includes(String(sub.id))}
                      onCheckedChange={() => toggleSubCategory(sub.id)}
                    />
                    <span className="text-sm">{sub.name}</span>
                  </label>
                ))
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div className="space-y-2">
        <Label htmlFor="company_name">Company Name</Label>
        <Input
          id="company_name"
          placeholder="Company name"
          value={String(form.company_name)}
          onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))}
        />
      </div>
      <div className="space-y-2">
        <Label>Vendor Type</Label>
        <Select
          value={String(form.vendor_type)}
          onValueChange={v =>
            setForm(p => ({ ...p, vendor_type: v as "individual" | "company" }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Individual / Company" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="individual">Individual</SelectItem>
            <SelectItem value="company">Company</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Logo Upload</Label>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-dashed bg-muted/30 p-4">
            <label className="flex flex-1 cursor-pointer items-center gap-2 text-muted-foreground hover:text-foreground">
              <Upload className="h-4 w-4" />
              <span className="text-sm">
                {(form.logo as File | null)?.name ?? "Select logo image"}
              </span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onImageChange}
              />
            </label>
          </div>
          {imagePreview && (
            <div className="relative h-24 w-24 overflow-hidden rounded-lg border bg-muted">
              <img
                src={imagePreview}
                alt="Logo"
                className="h-full w-full object-cover"
              />
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="short_description">Short Description</Label>
        <Textarea
          id="short_description"
          placeholder="Brief description"
          rows={3}
          value={String(form.short_description)}
          onChange={e =>
            setForm(p => ({ ...p, short_description: e.target.value }))
          }
        />
      </div>
    </div>
  );
}

function VendorFormStep2({
  form,
  setForm,
}: {
  form: typeof initialFormState;
  setForm: React.Dispatch<React.SetStateAction<typeof initialFormState>>;
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="contact_person_name">Contact Person Name</Label>
        <Input
          id="contact_person_name"
          placeholder="Full name"
          value={String(form.contact_person_name)}
          onChange={e =>
            setForm(p => ({ ...p, contact_person_name: e.target.value }))
          }
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="mobile_number">Mobile Number</Label>
          <Input
            id="mobile_number"
            placeholder="10-digit mobile"
            value={String(form.mobile_number)}
            maxLength={10}
            onChange={e => {
              const value = e.target.value.replace(/\D/g, "");
              setForm(p => ({ ...p, mobile_number: value }));
            }}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="email@example.com"
            value={String(form.email)}
            onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="office_address">Office Address</Label>
        <Textarea
          id="office_address"
          placeholder="Full address"
          rows={2}
          value={String(form.office_address)}
          onChange={e =>
            setForm(p => ({ ...p, office_address: e.target.value }))
          }
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="city">City</Label>
          <Input
            id="city"
            placeholder="City"
            value={String(form.city)}
            onChange={e => setForm(p => ({ ...p, city: e.target.value }))}
          />
        </div>
        <div className="space-y-2">
          <Label>State</Label>
          <Select
            value={String(form.state)}
            onValueChange={v => setForm(p => ({ ...p, state: v }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {INDIAN_STATES.map(s => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="pin_code">PIN Code</Label>
          <Input
            id="pin_code"
            placeholder="PIN"
            value={String(form.pin_code)}
            maxLength={6}
            onChange={e => {
              const value = e.target.value.replace(/\D/g, "");
              setForm(p => ({ ...p, pin_code: value }));
            }}
          />
        </div>
      </div>
    </div>
  );
}

function VendorFormStep3({
  form,
  setForm,
}: {
  form: typeof initialFormState;
  setForm: React.Dispatch<React.SetStateAction<typeof initialFormState>>;
}) {
  const [statesList, setStatesList] = useState<State[]>([]);
  const [loadingStates, setLoadingStates] = useState(true);
  const [stateOpen, setStateOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingStates(true);
    statesAPI
      .list()
      .then(data => {
        if (!cancelled) {
          setStatesList(Array.isArray(data) ? data : []);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingStates(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleArea = (stateName: string) => {
    const areas = (form.service_areas as string[]) || [];
    const next = areas.includes(stateName)
      ? areas.filter(a => a !== stateName)
      : [...areas, stateName];
    setForm(p => ({ ...p, service_areas: next }));
  };

  const serviceAreaNames = (form.service_areas as string[]) || [];
  const stateLabel =
    serviceAreaNames.length === 0
      ? "Select service areas (States)..."
      : serviceAreaNames.join(", ");

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Service Areas (Multi-select)</Label>
        <Popover open={stateOpen} onOpenChange={setStateOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-between font-normal"
              disabled={loadingStates}
            >
              <span className="truncate">
                {loadingStates ? "Loading..." : stateLabel}
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <div className="max-h-60 overflow-y-auto p-2">
              {statesList.length === 0 && !loadingStates ? (
                <p className="py-2 text-center text-sm text-muted-foreground">
                  No states found
                </p>
              ) : (
                statesList.map(s => (
                  <label
                    key={s.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-muted"
                  >
                    <Checkbox
                      checked={serviceAreaNames.includes(s.name)}
                      onCheckedChange={() => toggleArea(s.name)}
                    />
                    <span className="text-sm">{s.name}</span>
                  </label>
                ))
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div className="space-y-2">
        <Label>PAN India Service</Label>
        <Select
          value={String(form.pan_india_service)}
          onValueChange={v =>
            setForm(p => ({ ...p, pan_india_service: v as "yes" | "no" }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Yes / No" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function VendorFormStep4({
  form,
  setForm,
}: {
  form: typeof initialFormState;
  setForm: React.Dispatch<React.SetStateAction<typeof initialFormState>>;
}) {
  const tags = (form.services_offered as string[]) || [];
  const addTag = () => {
    const input = String(form.service_tag_input).trim();
    if (!input || tags.includes(input)) {
      return;
    }
    setForm(p => ({
      ...p,
      services_offered: [...tags, input],
      service_tag_input: "",
    }));
  };
  const removeTag = (t: string) => {
    setForm(p => ({ ...p, services_offered: tags.filter(x => x !== t) }));
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Services Offered (Tags)</Label>
        <div className="flex gap-2">
          <Input
            placeholder="Type and press Enter to add"
            value={String(form.service_tag_input)}
            onChange={e =>
              setForm(p => ({ ...p, service_tag_input: e.target.value }))
            }
            onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addTag())}
          />
          <Button type="button" variant="outline" onClick={addTag}>
            Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {tags.map(t => (
            <Badge key={t} variant="secondary" className="gap-1">
              {t}
              <button
                type="button"
                onClick={() => removeTag(t)}
                className="hover:text-destructive"
              >
                ×
              </button>
            </Badge>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="specialization">Specialization</Label>
        <Input
          id="specialization"
          placeholder="e.g. Hostel catering, Security systems"
          value={String(form.specialization)}
          onChange={e =>
            setForm(p => ({ ...p, specialization: e.target.value }))
          }
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="years_of_experience">Years of Experience</Label>
        <Input
          id="years_of_experience"
          type="number"
          min={0}
          onWheel={e => e.currentTarget.blur()}
          onKeyDown={e =>
            ["e", "E", "-", "+"].includes(e.key) && e.preventDefault()
          }
          placeholder="Number of years"
          value={String(form.years_of_experience)}
          onChange={e =>
            setForm(p => ({ ...p, years_of_experience: e.target.value }))
          }
        />
      </div>
      <div className="space-y-2">
        <Label>Hostel Projects Experience</Label>
        <Select
          value={String(form.hostel_projects_experience)}
          onValueChange={v =>
            setForm(p => ({
              ...p,
              hostel_projects_experience: v as "yes" | "no",
            }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Yes / No" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="past_projects">List of Past Projects (Text)</Label>
        <Textarea
          id="past_projects"
          placeholder="Describe or list past projects"
          rows={3}
          value={String(form.past_projects)}
          onChange={e =>
            setForm(p => ({ ...p, past_projects: e.target.value }))
          }
        />
      </div>
      <div className="space-y-2">
        <Label>Upload Past Projects (optional)</Label>
        <div className="rounded-lg border border-dashed bg-muted/30 p-4">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <Upload className="h-4 w-4" />
            <span>
              {(form.past_projects_file as File | null)?.name ?? "Select file"}
            </span>
            <input
              type="file"
              accept=".pdf,.doc,.docx,image/*"
              className="hidden"
              onChange={e =>
                setForm(p => ({
                  ...p,
                  past_projects_file: e.target.files?.[0] ?? null,
                }))
              }
            />
          </label>
        </div>
      </div>
    </div>
  );
}

function VendorFormStep5({
  form,
  setForm,
}: {
  form: typeof initialFormState;
  setForm: React.Dispatch<React.SetStateAction<typeof initialFormState>>;
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="empanelment_status">Empanelment Status</Label>
        <Input
          id="empanelment_status"
          placeholder="e.g. Pending, Approved"
          value={String(form.empanelment_status)}
          onChange={e =>
            setForm(p => ({ ...p, empanelment_status: e.target.value }))
          }
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="verification_status">Verification Status</Label>
        <Input
          id="verification_status"
          placeholder="e.g. Verified, Pending"
          value={String(form.verification_status)}
          onChange={e =>
            setForm(p => ({ ...p, verification_status: e.target.value }))
          }
        />
      </div>
      <div className="space-y-2">
        <Label>Documents Uploaded</Label>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-muted/30 p-4 transition-colors hover:border-gray-400 hover:bg-muted/50">
            <label className="flex flex-1 cursor-pointer items-center gap-2 text-muted-foreground hover:text-foreground">
              <Upload className="h-4 w-4 shrink-0" />
              <span className="text-sm">
                {(form.documents_uploaded_file as File | null)
                  ? (form.documents_uploaded_file as File).name
                  : "Select document (PDF, DOC, images)"}
              </span>
              <input
                type="file"
                accept=".pdf,.doc,.docx,image/*"
                className="hidden"
                onChange={e =>
                  setForm(p => ({
                    ...p,
                    documents_uploaded_file: e.target.files?.[0] ?? null,
                  }))
                }
              />
            </label>
          </div>
          {(form.documents_uploaded_file as File | null) && (
            <div className="flex items-center justify-between rounded-md border bg-muted/20 px-3 py-2 text-sm">
              <span className="truncate text-foreground">
                {(form.documents_uploaded_file as File).name}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() =>
                  setForm(p => ({ ...p, documents_uploaded_file: null }))
                }
              >
                Remove
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <Checkbox
          id="compliance_confirmation"
          checked={Boolean(form.compliance_confirmation)}
          onCheckedChange={c =>
            setForm(p => ({ ...p, compliance_confirmation: c === true }))
          }
        />
        <Label
          htmlFor="compliance_confirmation"
          className="cursor-pointer text-sm font-normal"
        >
          Compliance Confirmation
        </Label>
      </div>
      <div className="space-y-2">
        <Label htmlFor="admin_notes">Admin Notes</Label>
        <Textarea
          id="admin_notes"
          placeholder="Internal notes"
          rows={3}
          value={String(form.admin_notes)}
          onChange={e => setForm(p => ({ ...p, admin_notes: e.target.value }))}
        />
      </div>
    </div>
  );
}

function VendorFormStep6({
  form,
  setForm,
}: {
  form: typeof initialFormState;
  setForm: React.Dispatch<React.SetStateAction<typeof initialFormState>>;
}) {
  const toggleDay = (day: string) => {
    const days = (form.working_days as string[]) || [];
    const next = days.includes(day)
      ? days.filter(d => d !== day)
      : [...days, day];
    setForm(p => ({ ...p, working_days: next }));
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Available for Work</Label>
        <Select
          value={String(form.available_for_work)}
          onValueChange={v =>
            setForm(p => ({ ...p, available_for_work: v as "yes" | "no" }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Yes / No" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Working Days</Label>
        <div className="flex flex-wrap gap-2 rounded-md border p-3">
          {WORKING_DAYS.map(day => (
            <label key={day} className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={(form.working_days as string[])?.includes(day)}
                onCheckedChange={() => toggleDay(day)}
              />
              <span className="text-sm">{day}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="service_timings_from">Service Timings From</Label>
          <Input
            id="service_timings_from"
            type="time"
            value={String(form.service_timings_from)}
            onChange={e =>
              setForm(p => ({ ...p, service_timings_from: e.target.value }))
            }
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="service_timings_to">Service Timings To</Label>
          <Input
            id="service_timings_to"
            type="time"
            value={String(form.service_timings_to)}
            onChange={e =>
              setForm(p => ({ ...p, service_timings_to: e.target.value }))
            }
          />
        </div>
      </div>
    </div>
  );
}

function parseStringArray(
  value: string[] | (string | number)[] | string | null | undefined
): string[] {
  if (Array.isArray(value)) {
    return value.map(String);
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value) as unknown;
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return value.trim() ? [value] : [];
    }
  }
  return [];
}

function vendorToForm(v: Vendor): typeof initialFormState {
  const panIndia =
    v.pan_india_service === true
      ? "yes"
      : v.pan_india_service === false
        ? "no"
        : "";
  const hostelExp =
    v.hostel_projects_experience === true
      ? "yes"
      : v.hostel_projects_experience === false
        ? "no"
        : "";
  const available =
    v.available_for_work === true
      ? "yes"
      : v.available_for_work === false
        ? "no"
        : "";
  let service_timings_from = "";
  let service_timings_to = "";
  if (
    typeof v.service_timings === "string" &&
    v.service_timings.includes("-")
  ) {
    const parts = v.service_timings.split("-").map(s => s.trim());
    if (parts.length >= 2) {
      service_timings_from = parts[0];
      service_timings_to = parts[1];
    }
  }
  return {
    ...initialFormState,
    vendor_name: v.vendor_name ?? "",
    category: parseStringArray(v.vendor_category ?? v.category),
    sub_category: parseStringArray(v.vendor_sub_category),
    company_name: v.company_name ?? "",
    vendor_type: (v.vendor_type as "" | "individual" | "company") ?? "",
    logo: null,
    short_description: v.short_description ?? "",
    contact_person_name: v.contact_person_name ?? "",
    mobile_number: v.mobile_number ?? "",
    email: v.email ?? "",
    office_address: v.office_address ?? "",
    city: v.city ?? "",
    state: v.state ?? "",
    pin_code: v.pin_code ?? "",
    service_areas: parseStringArray(v.service_areas),
    pan_india_service: panIndia as "" | "yes" | "no",
    services_offered: parseStringArray(v.services_offered),
    service_tag_input: "",
    specialization: v.specialization ?? "",
    years_of_experience:
      v.years_of_experience !== null && v.years_of_experience !== undefined
        ? String(v.years_of_experience)
        : "",
    hostel_projects_experience: hostelExp as "" | "yes" | "no",
    past_projects: Array.isArray(v.past_projects)
      ? JSON.stringify(v.past_projects, null, 2)
      : (v.past_projects ?? ""),
    past_projects_file: null,
    documents_uploaded: v.documents_uploaded ?? "",
    documents_uploaded_file: null,
    empanelment_status: v.empanelment_status ?? "",
    verification_status: v.verification_status ?? "",
    compliance_confirmation: Boolean(v.compliance_confirmation),
    admin_notes: v.admin_notes ?? "",
    available_for_work: available as "" | "yes" | "no",
    working_days: parseStringArray(v.working_days),
    service_timings_from,
    service_timings_to,
  };
}

export default function Vendors() {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingVendor, setViewingVendor] = useState<Vendor | null>(null);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [loadingVendor, setLoadingVendor] = useState(false);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [form, setForm] = useState(initialFormState);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { can } = useAuth();

  const canWrite = can("vendors", "write");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [search, setSearch] = useState("");

  const loadVendors = useCallback(
    async (params?: PaginationParams) => {
      setLoading(true);
      try {
        const fetchParams: PaginationParams = {
          page: params?.page ?? currentPage,
          page_size: params?.page_size ?? pageSize,
          ...params,
        };
        if (search) {
          fetchParams.search = search;
        }

        const res = await vendorsAPI.list(fetchParams);
        if (res && typeof res === "object") {
          const list = res.results ?? res.data ?? [];
          setVendors(Array.isArray(list) ? list : []);
          const count =
            res.count ??
            (res as any).total_count ??
            (Array.isArray(list) ? list.length : 0);
          setTotalItems(count);
          setTotalPages(Math.ceil(count / (fetchParams.page_size ?? pageSize)));
        }
      } catch {
        toast.error("Failed to load vendors");
        setVendors([]);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize, search]
  );

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  const resetForm = () => {
    setForm(initialFormState);
    setImagePreview(null);
    setFieldErrors({});
    setStep(1);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setForm(p => ({ ...p, logo: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const openAddModal = () => {
    resetForm();
    setEditingId(null);
    setIsDialogOpen(true);
  };

  const openViewModal = async (v: Vendor) => {
    setLoadingVendor(true);
    setIsViewDialogOpen(true);
    setViewingVendor(null);
    try {
      const data = await vendorsAPI.get(v.id);
      setViewingVendor(data);
    } catch {
      toast.error("Failed to load vendor details");
      setIsViewDialogOpen(false);
    } finally {
      setLoadingVendor(false);
    }
  };

  const openEditModal = async (v: Vendor) => {
    setEditingId(v.id);
    setLoadingVendor(true);
    try {
      const data = await vendorsAPI.get(v.id);
      setForm(vendorToForm(data));
      const logoUrl = data.logo ?? data.logo_url;
      setImagePreview(logoUrl && typeof logoUrl === "string" ? logoUrl : null);
      setStep(1);
      setIsDialogOpen(true);
    } catch {
      toast.error("Failed to load vendor for editing");
    } finally {
      setLoadingVendor(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetForm();
      setEditingId(null);
    }
    setIsDialogOpen(open);
  };

  const validateStep = (currentStep: number) => {
    const errors: Record<string, string> = {};
    if (currentStep === 1) {
      if (!String(form.vendor_name).trim()) {
        errors.vendor_name = "Vendor Name is required";
      }
    }
    // Add validations for other steps if needed
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(step)) {
      setStep(s => s + 1);
    }
  };

  const buildPayload = (): VendorCreatePayload => {
    const from = String(form.service_timings_from);
    const to = String(form.service_timings_to);
    const service_timings =
      from && to ? `${from} - ${to}` : from || to || undefined;

    let past_projects: string | PastProject[] | undefined =
      String(form.past_projects).trim() || undefined;
    if (past_projects) {
      try {
        const parsed = JSON.parse(past_projects);
        if (Array.isArray(parsed)) {
          past_projects = parsed as PastProject[];
        }
      } catch {
        // keep as string
      }
    }

    const documentsFile = form.documents_uploaded_file as File | null;
    const documentsUrl = String(form.documents_uploaded).trim();
    const documents_uploaded = documentsFile ?? (documentsUrl || undefined);

    return {
      vendor_name: String(form.vendor_name).trim(),
      vendor_category: (form.category as string[])?.length
        ? (form.category as string[])
        : undefined,
      vendor_sub_category: (form.sub_category as string[])?.length
        ? (form.sub_category as string[])
        : undefined,
      company_name: String(form.company_name).trim() || undefined,
      vendor_type:
        (form.vendor_type as "" | "individual" | "company") || undefined,
      logo_image: (form.logo as File | null) ?? undefined,
      short_description: String(form.short_description).trim() || undefined,
      contact_person_name: String(form.contact_person_name).trim() || undefined,
      mobile_number: String(form.mobile_number).trim() || undefined,
      email: String(form.email).trim() || undefined,
      office_address: String(form.office_address).trim() || undefined,
      city: String(form.city).trim() || undefined,
      state: String(form.state).trim() || undefined,
      pin_code: String(form.pin_code).trim() || undefined,
      service_areas: (form.service_areas as string[])?.length
        ? (form.service_areas as string[])
        : undefined,
      pan_india_service: form.pan_india_service === "yes" ? true : undefined,
      services_offered: (form.services_offered as string[])?.length
        ? (form.services_offered as string[])
        : undefined,
      specialization: String(form.specialization).trim() || undefined,
      years_of_experience: String(form.years_of_experience).trim() || undefined,
      hostel_projects_experience:
        form.hostel_projects_experience === "yes" ? true : undefined,
      past_projects,
      empanelment_status: String(form.empanelment_status).trim() || undefined,
      verification_status: String(form.verification_status).trim() || undefined,
      compliance_confirmation:
        form.compliance_confirmation === true ? true : undefined,
      admin_notes: String(form.admin_notes).trim() || undefined,
      available_for_work: form.available_for_work === "yes" ? true : undefined,
      working_days: (form.working_days as string[])?.length
        ? (form.working_days as string[])
        : undefined,
      service_timings,
      documents_uploaded,
    };
  };

  const handleSave = async () => {
    if (!String(form.vendor_name).trim()) {
      toast.error("Vendor Name is required.");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (editingId !== null && editingId !== undefined) {
        await vendorsAPI.update(editingId, payload);
        toast.success("Vendor updated successfully!");
      } else {
        await vendorsAPI.create(payload);
        toast.success("Vendor added successfully!");
      }
      await loadVendors();
      setIsDialogOpen(false);
      resetForm();
      setEditingId(null);
    } catch {
      toast.error(
        editingId !== null && editingId !== undefined
          ? "Failed to update vendor"
          : "Failed to add vendor"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (id: string | number) => setDeleteId(id);
  const handleDeleteConfirm = async () => {
    if (deleteId === null) {
      return;
    }
    try {
      await vendorsAPI.delete(deleteId);
      toast.success("Vendor deleted successfully!");
      await loadVendors();
      setDeleteId(null);
    } catch {
      toast.error("Failed to delete vendor");
    }
  };

  const vendorsTableColumns = useMemo<TableColumnDef<Vendor>[]>(
    () => [
      {
        id: "sr",
        label: "Sr. No",
        width: 72,
        alwaysVisible: true,
        renderCell: (_row, rowIndex) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
      },
      {
        id: "vendor",
        label: "Vendor Name",
        width: 260,
        renderCell: v => (
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 shrink-0 overflow-hidden rounded border bg-muted">
              {v.logo || v.logo_url ? (
                <img
                  src={v.logo ?? v.logo_url ?? ""}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                  Logo
                </div>
              )}
            </div>
            <div className="flex flex-col">
              <span className="font-medium">{v.vendor_name}</span>
              <span className="text-xs text-muted-foreground">
                {v.company_name}
              </span>
            </div>
          </div>
        ),
      },
      {
        id: "type",
        label: "Type",
        width: 140,
        renderCell: v => formatVendorArray(v.vendor_category_names),
      },
      {
        id: "contact",
        label: "Contact",
        width: 160,
        renderCell: v => (
          <div className="flex flex-col text-sm">
            <span>{v.contact_person_name}</span>
            <span className="text-muted-foreground">{v.mobile_number}</span>
          </div>
        ),
      },
      {
        id: "available",
        label: "Available",
        width: 120,
        renderCell: v => (
          <Badge variant="outline" className="capitalize">
            {v.verification_status ?? "Pending"}
          </Badge>
        ),
      },
      {
        id: "actions",
        label: "Action",
        width: 120,
        alwaysVisible: true,
        headerClassName: "text-right",
        cellClassName: "text-right",
        renderCell: v => (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => openViewModal(v)}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => openEditModal(v)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={() => handleDeleteClick(v.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [currentPage, pageSize]
  );

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <VendorFormStep1
            form={form}
            setForm={setForm}
            imagePreview={imagePreview}
            onImageChange={handleImageChange}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
          />
        );
      case 2:
        return <VendorFormStep2 form={form} setForm={setForm} />;
      case 3:
        return <VendorFormStep3 form={form} setForm={setForm} />;
      case 4:
        return <VendorFormStep4 form={form} setForm={setForm} />;
      case 5:
        return <VendorFormStep5 form={form} setForm={setForm} />;
      case 6:
        return <VendorFormStep6 form={form} setForm={setForm} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Vendors Management
          </h1>
          <p className="text-muted-foreground">
            Add and manage vendors (super admin)
          </p>
        </div>
        {canWrite && (
          <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
              <Button className="gap-2" onClick={openAddModal}>
                <Plus className="h-4 w-4" />
                Add Vendor
              </Button>
            </DialogTrigger>
            <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden">
              <DialogHeader>
                <DialogTitle>
                  {editingId !== null && editingId !== undefined
                    ? "Edit Vendor"
                    : "Add New Vendor"}
                </DialogTitle>
              </DialogHeader>

              {/* Horizontal stepper: circles + connectors, labels below */}
              <div className="border-b border-gray-200 pb-5">
                <div className="flex w-full items-start">
                  {STEPS.map((s, i) => {
                    const isActive = step === s.id;
                    return (
                      <Fragment key={s.id}>
                        <button
                          type="button"
                          onClick={() => {
                            if (s.id > step) {
                              if (validateStep(step)) {
                                setStep(s.id);
                              }
                            } else {
                              setStep(s.id);
                            }
                          }}
                          className="flex flex-shrink-0 flex-col items-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                          <span
                            className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                              isActive
                                ? "bg-[#8B3A3A] text-white"
                                : "border-2 border-gray-300 bg-transparent text-gray-400"
                            }`}
                          >
                            {s.id}
                          </span>
                          <span className="mt-1.5 max-w-[72px] text-center text-xs font-medium text-gray-700">
                            {s.title}
                          </span>
                        </button>
                        {i < STEPS.length - 1 && (
                          <div
                            className="flex h-9 min-w-[8px] flex-1 items-center self-start px-0.5"
                            aria-hidden
                          >
                            <div className="h-px w-full bg-gray-200" />
                          </div>
                        )}
                      </Fragment>
                    );
                  })}
                </div>
              </div>

              <div className="h-[500px] space-y-4 overflow-y-auto px-2">
                {renderStep()}
              </div>

              <div className="mt-4 flex shrink-0 justify-between border-t pt-4">
                <div>
                  {step > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(s => s - 1)}
                      disabled={saving}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  {step < 6 ? (
                    <Button type="button" onClick={handleNextStep}>
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  ) : null}
                  {step === 6 ? (
                    <Button onClick={handleSave} disabled={saving}>
                      {saving && <CustomLoader size="xs" className="mr-2" />}
                      {editingId !== null && editingId !== undefined
                        ? "Update Vendor"
                        : "Save Vendor"}
                    </Button>
                  ) : (
                    step === 1 && (
                      <Button
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                    )
                  )}
                  {step > 1 && step < 6 && (
                    <Button
                      variant="outline"
                      onClick={() => handleOpenChange(false)}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        <VendorDetailsModal
          open={isViewDialogOpen}
          onOpenChange={setIsViewDialogOpen}
          vendor={viewingVendor}
          loading={loadingVendor}
          showAdminFields
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Search & Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="relative min-w-[300px] flex-1">
              <Input
                placeholder="Search vendors..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e =>
                  e.key === "Enter" &&
                  (setCurrentPage(1), loadVendors({ page: 1 }))
                }
              />
            </div>
            <Button
              onClick={() => {
                setCurrentPage(1);
                loadVendors({ page: 1 });
              }}
            >
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <CustomLoader size="md" />
            </div>
          ) : (
            <DataTable
              title={`All Vendors (${vendors.length})`}
              tableId="super-admin-vendors"
              columns={vendorsTableColumns}
              data={vendors}
              getRowKey={v => v.id}
              emptyMessage='No vendors yet. Click "Add Vendor" to create one.'
            />
          )}

          {totalItems > 0 && (
            <div className="mt-4 border-t pt-4">
              <CommonPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalCount={totalItems}
                onPageChange={setCurrentPage}
                onPageSizeChange={size => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={deleteId !== null && deleteId !== undefined}
        onOpenChange={open => !open && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete vendor</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              vendor.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="text-destructive-foreground bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
